import React from 'react';
import { Palette, ArrowRight, Map, TrendingUp, Theater, Sparkles, Search, Wand2, Timer } from 'lucide-react';
import HeroImage from '../../components/HeroImage';

function WebsiteCreation() {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=1920&h=1080" 
              alt="Modern web development workspace"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center mb-8">
              <Palette className="h-16 w-16 text-[#00ffff]" />
            </div>
            <h1 className="text-5xl font-bold text-center text-white mb-6">Website Creation & Curation</h1>
            <p className="text-xl text-white/80 text-center max-w-3xl mx-auto">
              We'll help you design and optimize websites that amaze users with exceptional experiences and drive conversions. Our final products are delivered to you in days, not weeks or months.
            </p>
          </div>
        </div>
      </section>

      {/* Why Trust Atlas Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white mb-12">Why Trust Atlas AI with Your Website?</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="white-holographic-card p-8 rounded-xl">
            <div className="mb-6">
              <div className="relative">
                <Sparkles className="h-12 w-12 text-[#00ffff]" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">It's All About You</h3>
            <p className="text-gray-600">
              We take the time to understand your goals and create a website that's truly reflective of your brand. We never rely on generic templates.
            </p>
          </div>
          <div className="white-holographic-card p-8 rounded-xl">
            <div className="mb-6">
              <div className="relative">
                <Theater className="h-12 w-12 text-[#00ffff]" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Your Story, Center Stage</h3>
            <p className="text-gray-600">
              Every brand has a story, and we make sure yours comes alive in a way that connects with your audience.
            </p>
          </div>
          <div className="white-holographic-card p-8 rounded-xl">
            <div className="mb-6">
              <div className="relative">
                <TrendingUp className="h-12 w-12 text-[#00ffff]" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Built for the Future</h3>
            <p className="text-gray-600">
              We create sites that don't just meet your needs today but are flexible enough to grow with your business tomorrow.
            </p>
          </div>
          <div className="white-holographic-card p-8 rounded-xl">
            <div className="mb-6">
              <div className="relative">
                <Map className="h-12 w-12 text-[#00ffff]" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">We're Here for the Long Haul</h3>
            <p className="text-gray-600">
              We're more than just developers; we're partners invested in your success, offering ongoing support and guidance whenever you need it.
            </p>
          </div>
          <div className="white-holographic-card p-8 rounded-xl">
            <div className="mb-6">
              <div className="relative">
                <Timer className="h-12 w-12 text-[#00ffff]" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Speed of Execution</h3>
            <p className="text-gray-600">
              Once the development process is complete, we use our proprietary methods to bring your dreams into reality in hours and days, not weeks or months. We go above and beyond to deliver high quality, online presence in a fraction of the time of other developers.
            </p>
          </div>
        </div>
      </div>

      {/* How We Bring Your Vision to Life Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white mb-12">How We Bring Your Vision to Life</h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div className="white-holographic-card p-8 rounded-xl">
            <div className="mb-6">
              <Search className="h-12 w-12 text-[#00ffff]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Understanding Your Brand</h3>
            <p className="text-gray-600">
              Your brand is one of a kind, and our process begins by exploring what makes it special. This collaborative discovery phase helps us truly understand your needs and ensures that every decision moving forward reflects your vision.
            </p>
          </div>
          <div className="white-holographic-card p-8 rounded-xl">
            <div className="mb-6">
              <Wand2 className="h-12 w-12 text-[#00ffff]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Turning Your Vision into Reality</h3>
            <p className="text-gray-600">
              This is where the magic happens. Your website is a stage for your story, and we ensure it's told authentically. We use our expertise to turn your story into a visually stunning and functional digital presence. From dynamic layouts to intuitive navigation, we craft a site that not only meets your technical needs but also captivates your audience with a story that's uniquely yours.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 py-24">
        <div className="white-holographic-card p-12 rounded-2xl text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Ready for Your Upgrade?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's make your best impression into the Main Event!
          </p>
          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-[#00ffff] text-white rounded-lg hover:bg-[#ff0000] transition-colors font-semibold inline-flex items-center mx-auto"
          >
            Start Your Project
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default WebsiteCreation;